import { useCallback, useEffect, useRef } from 'react';
import { trackEvent } from '../analytics';

// The one definition of "the reader saw this section", shared by every page
// that emits `section_view`: the five acquisition doors (via useDoorPage) and
// the long-form guides.
//
// These constants are the MEASUREMENT CONTRACT, not an implementation detail.
// GA4 compares section engagement across those pages, so a page measuring
// "seen" by a different rule silently corrupts the comparison — nothing looks
// broken, the numbers are just no longer about the same thing. That is the
// argument useDoorPage already makes for the door funnel; it applies just as
// well between a door and a guide, so the rule lives here once.
//
// The rule, and why each half exists:
//  - RATIO GATE. 40% of the section is on screen. The extra thresholds below
//    0.4 exist so the observer re-reports as a section scrolls in, rather than
//    only at the single 0.4 crossing.
//  - HALF-SCREEN FALLBACK. A section taller than the viewport can never reach
//    a 0.4 ratio, so "half the screen is this section" counts as seen too.
//    Without it, long sections would never report.
//  - ONCE PER SECTION. `fired` plus `unobserve` means scrolling back up cannot
//    double-count a section the reader already passed.

export const SECTION_VIEW_RATIO = 0.4;
export const SECTION_VIEW_ROOT_FRACTION = 0.5;
export const SECTION_VIEW_THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4];

/**
 * Watch `sectionIds` and emit `section_view` once per section.
 *
 * @param {string[]} sectionIds  ids to observe; each element is registered via
 *                               the returned ref setter and must carry a
 *                               matching `data-section-id` attribute.
 * @param {Object}   [eventParams] merged into every `section_view` payload —
 *                               e.g. `{ door }` on an acquisition door.
 * @returns {(id: string) => (el: Element|null) => void} ref setter for a section
 */
export default function useSectionViewTracking(sectionIds, eventParams) {
  const sectionRefs = useRef({});

  // Same trick usePageMeta uses for jsonLd: a serialized key is stable across
  // renders, so callers can pass an object literal without re-creating the
  // observer on every render, while a real change to `door` still resets it.
  const paramsKey = JSON.stringify(eventParams || null);
  const paramsRef = useRef(eventParams);
  paramsRef.current = eventParams;

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const fired = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const rootHeight = entry.rootBounds ? entry.rootBounds.height : 0;
          const visibleEnough =
            entry.intersectionRatio >= SECTION_VIEW_RATIO ||
            (rootHeight > 0 &&
              entry.intersectionRect &&
              entry.intersectionRect.height >= rootHeight * SECTION_VIEW_ROOT_FRACTION);
          if (entry.isIntersecting && visibleEnough) {
            const id = entry.target.getAttribute('data-section-id');
            if (id && !fired.has(id)) {
              fired.add(id);
              trackEvent('section_view', { section_id: id, ...(paramsRef.current || {}) });
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { threshold: SECTION_VIEW_THRESHOLDS }
    );
    sectionIds.forEach((id) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sectionIds, paramsKey]);

  return useCallback(
    (id) => (el) => {
      sectionRefs.current[id] = el;
    },
    []
  );
}
